import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ChevronDown, FileText, Loader2, MessageSquare, Paperclip, Send, StickyNote,
} from "lucide-react";
import { API, BACKEND_URL } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";

const STATUS_META = {
  new: { label: "Neu", cls: "bg-sky-500/10 text-sky-300" },
  in_review: { label: "In Prüfung", cls: "bg-amber-500/10 text-amber-300" },
  accepted: { label: "Angenommen", cls: "bg-emerald-500/10 text-emerald-300" },
  rejected: { label: "Abgelehnt", cls: "bg-rose-500/10 text-rose-300" },
};
const STATUSES = ["new", "in_review", "accepted", "rejected"];

const avatarHead = (name) =>
  `https://www.habbo.de/habbo-imaging/avatarimage?user=${encodeURIComponent(name)}&direction=2&head_direction=3&size=m&headonly=1`;

const fmtDate = (iso) => new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

const ApplicationsPanel = ({ token }) => {
  const { toast } = useToast();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [chatMsg, setChatMsg] = useState("");
  const [sendingMsg, setSendingMsg] = useState(false);

  const load = async (status = filter) => {
    try {
      const r = await axios.get(`${API}/cms/applications`, { ...auth, params: status ? { status } : {} });
      setData(r.data);
    } catch { setData({ items: [], counts: {} }); }
  };
  useEffect(() => { load(filter); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (app, status) => {
    try {
      await axios.patch(`${API}/cms/applications/${app.id}`, { status }, auth);
      toast({ title: "Status aktualisiert", description: `${app.habbo_name} → ${STATUS_META[status].label}` });
      await load();
    } catch { toast({ title: "Fehler", description: "Status-Wechsel fehlgeschlagen", variant: "destructive" }); }
  };

  const sendChat = async (app) => {
    if (!chatMsg.trim()) return;
    setSendingMsg(true);
    try {
      await axios.post(`${API}/jobs/applications/${app.id}/messages`, { text: chatMsg.trim() }, auth);
      setChatMsg("");
      toast({ title: "Nachricht gesendet", description: `${app.habbo_name} wurde im Postfach benachrichtigt.` });
      await load();
    } catch { toast({ title: "Fehler", description: "Nachricht fehlgeschlagen", variant: "destructive" }); }
    finally { setSendingMsg(false); }
  };

  const addNote = async (app) => {
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      await axios.post(`${API}/cms/applications/${app.id}/notes`, { text: note.trim() }, auth);
      setNote("");
      await load();
    } catch { toast({ title: "Fehler", description: "Notiz fehlgeschlagen", variant: "destructive" }); }
    finally { setSavingNote(false); }
  };

  if (data === null) return <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>;

  return (
    <div data-testid="applications-panel">
      <h2 className="flex items-center gap-2 text-xl font-bold text-white"><FileText className="h-5 w-5 text-[#9d7bff]" /> Bewerbungen</h2>
      <p className="mt-0.5 text-xs text-zinc-500">Status-Änderungen benachrichtigen den Bewerber automatisch im Habthek-Postfach.</p>

      {/* Filter */}
      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={() => setFilter(null)} data-testid="applications-filter-all" className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ${!filter ? "bg-[#7c4dff] text-white" : "border border-white/10 bg-white/5 text-zinc-400 hover:text-white"}`}>
          Alle
        </button>
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setFilter(s === filter ? null : s)} data-testid={`applications-filter-${s}`} className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ${filter === s ? "bg-[#7c4dff] text-white" : "border border-white/10 bg-white/5 text-zinc-400 hover:text-white"}`}>
            {STATUS_META[s].label} ({data.counts?.[s] ?? 0})
          </button>
        ))}
      </div>

      {/* Liste */}
      <div className="mt-4 space-y-2">
        {data.items.length === 0 && (
          <div className="rounded-xl border border-white/10 bg-[#0f141b] px-4 py-10 text-center text-sm text-zinc-500" data-testid="applications-empty">Keine Bewerbungen{filter ? " mit diesem Status" : ""}.</div>
        )}
        {data.items.map((a) => {
          const meta = STATUS_META[a.status] || STATUS_META.new;
          const isOpen = expanded === a.id;
          return (
            <div key={a.id} className="overflow-hidden rounded-xl border border-white/10 bg-[#0f141b]" data-testid={`application-${a.id}`}>
              <button onClick={() => { setExpanded(isOpen ? null : a.id); setNote(""); setChatMsg(""); }} data-testid={`application-toggle-${a.id}`} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.03]">
                <img src={avatarHead(a.habbo_name)} alt={a.habbo_name} className="h-9 w-9 shrink-0" style={{ imageRendering: "pixelated" }} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-white">{a.habbo_name}</div>
                  <div className="truncate text-[11px] text-zinc-500">{a.job_title} · {fmtDate(a.created_at)}</div>
                </div>
                {a.attachment_url && <Paperclip className="h-3.5 w-3.5 shrink-0 text-zinc-500" />}
                <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${meta.cls}`}>{meta.label}</span>
                <ChevronDown className={`h-4 w-4 text-zinc-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="border-t border-white/5 px-4 py-4">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Nachricht</div>
                  <div className="mt-1.5 whitespace-pre-wrap rounded-lg bg-white/5 px-3 py-2.5 text-sm text-zinc-300" data-testid={`application-message-${a.id}`}>{a.message}</div>

                  <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-zinc-400">
                    <Link to={`/u/${encodeURIComponent(a.habbo_name)}`} className="text-[#b39bff] hover:text-white">Profil ansehen</Link>
                    {a.attachment_url && (
                      <a href={`${BACKEND_URL}${a.attachment_url}`} target="_blank" rel="noreferrer" data-testid={`application-attachment-${a.id}`} className="inline-flex items-center gap-1 text-[#b39bff] hover:text-white">
                        <Paperclip className="h-3 w-3" /> {a.attachment_name || "Anhang"}
                      </a>
                    )}
                  </div>

                  {/* Status setzen */}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {STATUSES.map((s) => (
                      <button
                        key={s}
                        onClick={() => setStatus(a, s)}
                        disabled={a.status === s}
                        data-testid={`application-status-${s}-${a.id}`}
                        className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${a.status === s ? `${STATUS_META[s].cls} cursor-default ring-1 ring-inset ring-white/20` : "border border-white/10 bg-white/5 text-zinc-400 hover:text-white"}`}
                      >
                        {STATUS_META[s].label}
                      </button>
                    ))}
                  </div>

                  {/* Austausch mit dem Bewerber */}
                  <div className="mt-4">
                    <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                      <MessageSquare className="h-3 w-3" /> Austausch mit dem Bewerber (sichtbar für {a.habbo_name})
                    </div>
                    <div className="mt-2 space-y-1.5">
                      {(a.messages || []).length === 0 && (
                        <div className="py-2 text-center text-xs text-zinc-600">Noch keine Nachrichten.</div>
                      )}
                      {(a.messages || []).map((m) => (
                        <div key={m.id} className={`rounded-lg px-3 py-2 text-sm ${m.is_team ? "bg-[#7c4dff]/10 text-zinc-200" : "bg-white/5 text-zinc-300"}`}>
                          <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                            {m.is_team ? `Team (${m.author})` : m.author} · {fmtDate(m.created_at)}
                          </div>
                          <div className="whitespace-pre-wrap">{m.text}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 flex gap-2">
                      <Input value={chatMsg} onChange={(e) => setChatMsg(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") sendChat(a); }} placeholder="Nachricht an den Bewerber…" data-testid={`application-chat-input-${a.id}`} className="bg-[#10141b] border-white/10 text-zinc-200" />
                      <Button onClick={() => sendChat(a)} disabled={sendingMsg || !chatMsg.trim()} data-testid={`application-chat-send-${a.id}`} className="btn-primary rounded-lg px-3 py-2 text-sm">
                        {sendingMsg ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  {/* Interne Notizen */}
                  <div className="mt-4">
                    <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                      <StickyNote className="h-3 w-3" /> Interne Notizen (nur Team)
                    </div>
                    <div className="mt-2 space-y-1.5">
                      {(a.notes || []).map((n) => (
                        <div key={n.id} className="rounded-lg bg-[#ff9800]/5 px-3 py-2 text-xs text-zinc-300" data-testid={`application-note-${n.id}`}>
                          <span className="font-semibold text-[#ffb74d]">{n.by}</span>
                          <span className="ml-2 text-zinc-600">{fmtDate(n.created_at)}</span>
                          <div className="mt-0.5 whitespace-pre-wrap">{n.text}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 flex gap-2">
                      <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Notiz hinzufügen…" data-testid={`application-note-input-${a.id}`} className="bg-[#10141b] border-white/10 text-zinc-200" />
                      <Button onClick={() => addNote(a)} disabled={savingNote || !note.trim()} data-testid={`application-note-save-${a.id}`} className="btn-primary rounded-lg px-3 py-2 text-sm">
                        {savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ApplicationsPanel;
